/**
 * Sort control for the Tasks list screens.
 *
 * One quiet chip that reads as the current state ("Due ↑") and opens a menu
 * of the sort keys. Re-picking the active key flips direction; picking a
 * different key adopts that key's natural direction.
 *
 * Always the neutral outlined chip — the accent ("engaged") state was
 * removed when the control moved from one-per-page into each section
 * header: with up to five of these on screen at once, a highlighted chip
 * would read as "something's wrong here" rather than "this is sorted".
 *
 * Radix DropdownMenu handles keyboard navigation, Esc, click-outside and
 * focus return. The trigger reuses the global `kos-filter-chip` classes from
 * packages/ui/src/tokens.css so it matches the filter chips beside it.
 */

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Icon } from '@k-os/ui';
import {
  SORT_KEYS,
  naturalDir,
  sortKeyLabel,
  type SortDir,
  type TaskSort,
} from '../lib/task-sort';
import styles from './SortMenu.module.css';

export interface SortMenuProps {
  value: TaskSort;
  onChange: (next: TaskSort) => void;
  /**
   * The list this control sorts, e.g. "Overdue". Used only in the aria-label:
   * with up to five of these on one screen, "Sort: Priority ascending" alone
   * doesn't say which list it belongs to.
   */
  label?: string;
}

// `ICON_PATHS` in packages/ui has no `sort` glyph; `arrowUp` / `arrowDown`
// carry the direction, which is the part that needs to be legible anyway.
const DIR_ICON: Record<SortDir, 'arrowUp' | 'arrowDown'> = {
  asc: 'arrowUp',
  desc: 'arrowDown',
};
const DIR_WORD: Record<SortDir, string> = { asc: 'ascending', desc: 'descending' };

export function SortMenu({ value, onChange, label }: SortMenuProps) {
  const isDefault = value.key === 'native';
  const what = label ? `Sort ${label}` : 'Sort';

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={`kos-filter-chip ${styles.trigger}`}
          aria-label={
            isDefault
              ? `${what}: default order`
              : `${what}: ${sortKeyLabel(value.key)} ${DIR_WORD[value.dir]}`
          }
        >
          {sortKeyLabel(value.key)}
          {!isDefault && <Icon name={DIR_ICON[value.dir]} size={12} />}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={styles.content}
          side="bottom"
          align="end"
          sideOffset={6}
          collisionPadding={20}
        >
          <div className={styles.header}>Sort by</div>
          {SORT_KEYS.map((k) => {
            const active = k.key === value.key;
            return (
              <DropdownMenu.Item
                key={k.key}
                className={`${styles.item} ${active ? styles.itemActive : ''}`}
                onSelect={() =>
                  onChange(
                    active
                      ? // Re-picking the active key flips direction. `native`
                        // has no direction, so it stays put.
                        { key: k.key, dir: k.key === 'native' ? value.dir : value.dir === 'asc' ? 'desc' : 'asc' }
                      : { key: k.key, dir: naturalDir(k.key) },
                  )
                }
              >
                <span>{k.label}</span>
                {active && k.key !== 'native' && <Icon name={DIR_ICON[value.dir]} size={12} />}
              </DropdownMenu.Item>
            );
          })}
          <div className={styles.hint}>Pick the active key again to flip direction</div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
